import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Clock, MapPin, Plus, Minus } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';

interface TimeZoneInfo {
  id: string;
  name: string;
  offset: string;
  time: string;
  date: string;
  isDST: boolean;
}

interface WorldClock {
  id: string;
  timezone: string;
  label: string;
}

export const TimezoneConverterTool: React.FC = () => {
  const { t } = useTranslation();
  const [sourceTime, setSourceTime] = useState('');
  const [sourceTimezone, setSourceTimezone] = useState('UTC');
  const [targetTimezone, setTargetTimezone] = useState('America/New_York');
  const [convertedTime, setConvertedTime] = useState<TimeZoneInfo | null>(null);
  const [worldClocks, setWorldClocks] = useState<WorldClock[]>([
    { id: '1', timezone: 'UTC', label: 'UTC' },
    { id: '2', timezone: 'America/New_York', label: 'New York' },
    { id: '3', timezone: 'Europe/London', label: 'London' },
    { id: '4', timezone: 'Asia/Tokyo', label: 'Tokyo' }
  ]);
  const [currentTime, setCurrentTime] = useState(new Date());

  const commonTimezones = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (New York)' },
    { value: 'America/Chicago', label: 'Central Time (Chicago)' },
    { value: 'America/Denver', label: 'Mountain Time (Denver)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (London)' },
    { value: 'Europe/Paris', label: 'Central European Time (Paris)' },
    { value: 'Europe/Berlin', label: 'Central European Time (Berlin)' },
    { value: 'Europe/Moscow', label: 'Moscow Time' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (Tokyo)' },
    { value: 'Asia/Shanghai', label: 'China Standard Time (Shanghai)' },
    { value: 'Asia/Kolkata', label: 'India Standard Time (Mumbai)' },
    { value: 'Asia/Dubai', label: 'Gulf Standard Time (Dubai)' },
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (Sydney)' },
    { value: 'Pacific/Auckland', label: 'New Zealand Time (Auckland)' },
    { value: 'America/Sao_Paulo', label: 'Brasília Time (São Paulo)' }
  ];

  const getTimezoneInfo = (timezone: string, date?: Date): TimeZoneInfo => {
    const targetDate = date || new Date();
    
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      const parts = formatter.formatToParts(targetDate);
      const formattedTime = `${parts.find(p => p.type === 'hour')?.value}:${parts.find(p => p.type === 'minute')?.value}:${parts.find(p => p.type === 'second')?.value}`;
      const formattedDate = `${parts.find(p => p.type === 'year')?.value}-${parts.find(p => p.type === 'month')?.value}-${parts.find(p => p.type === 'day')?.value}`;

      // Get timezone offset
      const offsetFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'longOffset'
      });
      const offsetParts = offsetFormatter.formatToParts(targetDate);
      const offset = offsetParts.find(p => p.type === 'timeZoneName')?.value || 'UTC';

      // Check if DST is active (simplified check)
      const jan = new Date(targetDate.getFullYear(), 0, 1);
      const jul = new Date(targetDate.getFullYear(), 6, 1);
      const janOffset = jan.getTimezoneOffset();
      const julOffset = jul.getTimezoneOffset();
      const isDST = Math.max(janOffset, julOffset) !== targetDate.getTimezoneOffset();

      return {
        id: timezone,
        name: timezone,
        offset,
        time: formattedTime,
        date: formattedDate,
        isDST
      };
    } catch (error) {
      return {
        id: timezone,
        name: timezone,
        offset: 'UTC',
        time: '00:00:00',
        date: '1970-01-01',
        isDST: false
      };
    }
  };

  const convertTime = () => {
    if (!sourceTime) {
      setConvertedTime(null);
      return;
    }

    try {
      // Parse the input time
      const [datePart, timePart] = sourceTime.includes('T') 
        ? sourceTime.split('T') 
        : [sourceTime.split(' ')[0], sourceTime.split(' ')[1]];
      
      if (!datePart || !timePart) {
        throw new Error('Invalid time format');
      }

      // Create date in source timezone
      const sourceDate = new Date(`${datePart}T${timePart}`);
      
      // Convert to target timezone
      const targetInfo = getTimezoneInfo(targetTimezone, sourceDate);
      setConvertedTime(targetInfo);
    } catch (error) {
      setConvertedTime(null);
    }
  };

  const setCurrentTimeInSource = () => {
    const now = new Date();
    const sourceInfo = getTimezoneInfo(sourceTimezone, now);
    setSourceTime(`${sourceInfo.date}T${sourceInfo.time}`);
  };

  const addWorldClock = () => {
    const newId = Date.now().toString();
    const newClock: WorldClock = {
      id: newId,
      timezone: 'UTC',
      label: 'New Clock'
    };
    setWorldClocks([...worldClocks, newClock]);
  };

  const removeWorldClock = (id: string) => {
    setWorldClocks(worldClocks.filter(clock => clock.id !== id));
  };

  const updateWorldClock = (id: string, updates: Partial<WorldClock>) => {
    setWorldClocks(worldClocks.map(clock => 
      clock.id === id ? { ...clock, ...updates } : clock
    ));
  };

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Auto-convert when inputs change
  useEffect(() => {
    convertTime();
  }, [sourceTime, sourceTimezone, targetTimezone]);

  return (
    <div className="space-y-6">
      {/* Time Converter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Globe className="w-5 h-5" />
            <span>Timezone Converter</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Source Time */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Source Timezone
                </label>
                <select
                  value={sourceTimezone}
                  onChange={(e) => setSourceTimezone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {commonTimezones.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date and Time
                </label>
                <div className="flex space-x-2">
                  <input
                    type="datetime-local"
                    value={sourceTime}
                    onChange={(e) => setSourceTime(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  <Button
                    onClick={setCurrentTimeInSource}
                    variant="outline"
                    size="sm"
                  >
                    Now
                  </Button>
                </div>
              </div>
            </div>

            {/* Target Time */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Target Timezone
                </label>
                <select
                  value={targetTimezone}
                  onChange={(e) => setTargetTimezone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {commonTimezones.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>

              {convertedTime && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {convertedTime.time}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {convertedTime.date}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {convertedTime.offset} {convertedTime.isDST ? '(DST)' : ''}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* World Clock */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5" />
              <span>World Clock</span>
            </div>
            <Button
              onClick={addWorldClock}
              variant="outline"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Clock
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {worldClocks.map((clock) => {
              const timeInfo = getTimezoneInfo(clock.timezone, currentTime);
              return (
                <div
                  key={clock.id}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                >
                  <div className="flex items-center justify-between mb-2">
                    <input
                      type="text"
                      value={clock.label}
                      onChange={(e) => updateWorldClock(clock.id, { label: e.target.value })}
                      className="font-medium text-sm bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-primary-500 rounded px-1"
                    />
                    {worldClocks.length > 1 && (
                      <Button
                        onClick={() => removeWorldClock(clock.id)}
                        variant="ghost"
                        size="sm"
                        className="p-1"
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  
                  <select
                    value={clock.timezone}
                    onChange={(e) => updateWorldClock(clock.id, { timezone: e.target.value })}
                    className="w-full text-xs mb-2 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    {commonTimezones.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {timeInfo.time}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {timeInfo.date}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-500">
                      {timeInfo.offset}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Timezone Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MapPin className="w-5 h-5" />
            <span>Major Timezones</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'UTC',
              'America/New_York',
              'America/Los_Angeles',
              'Europe/London',
              'Europe/Paris',
              'Asia/Tokyo',
              'Asia/Shanghai',
              'Australia/Sydney'
            ].map((timezone) => {
              const info = getTimezoneInfo(timezone, currentTime);
              const label = commonTimezones.find(tz => tz.value === timezone)?.label || timezone;
              
              return (
                <div
                  key={timezone}
                  className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                  onClick={() => {
                    setTargetTimezone(timezone);
                  }}
                >
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    {label.split(' (')[0]}
                  </div>
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {info.time}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    {info.offset}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Time Conversion:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Select source and target timezones</li>
                <li>• Enter date and time or use "Now" button</li>
                <li>• View converted time instantly</li>
                <li>• DST (Daylight Saving Time) is automatically handled</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">World Clock:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Add multiple timezone clocks</li>
                <li>• Customize clock labels</li>
                <li>• Real-time updates every second</li>
                <li>• Click timezone reference to set as target</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
